import type { Router } from 'vue-router';
import logger from '@/logger';

/**
 * Contrato com o `public/sw.js`, que pede a navegação quando a notificação é tocada com o app
 * aberto. `__tests__/public/swContratoPagina.spec.ts` confere que os dois usam o mesmo tipo.
 */
export const TIPO_MENSAGEM_DEEPLINK = 'acerto-pwa:abrir-deeplink';

interface MensagemDeeplink {
  tipo: typeof TIPO_MENSAGEM_DEEPLINK;
  caminho: string;
}

function ehCaminhoInterno(caminho: unknown): caminho is string {
  return typeof caminho === 'string' && caminho.startsWith('/') && !caminho.startsWith('//');
}

function ehMensagemDeeplink(dados: unknown): dados is MensagemDeeplink {
  if (typeof dados !== 'object' || dados === null) return false;
  const { tipo, caminho } = dados as Record<string, unknown>;
  return tipo === TIPO_MENSAGEM_DEEPLINK && ehCaminhoInterno(caminho);
}

/**
 * Navega pelo router quando o service worker pede, em vez de o SW abrir uma segunda janela do app.
 * Só um service worker da própria origem consegue enviar mensagem para `navigator.serviceWorker`, e
 * o caminho precisa ser interno, então a mensagem nunca leva o router para fora do portal. A
 * resposta pela porta confirma ao SW que não precisa abrir outra janela; sai antes da navegação
 * porque o SW espera pouco, e o guard do router ainda decide o destino (ex.: login sem sessão).
 */
export function escutarDeeplinkDoServiceWorker(router: Router): void {
  if (!('serviceWorker' in navigator)) return;

  navigator.serviceWorker.addEventListener('message', (evento: MessageEvent) => {
    if (!ehMensagemDeeplink(evento.data)) return;

    evento.ports[0]?.postMessage('navegando');
    router.push(evento.data.caminho).catch((error: unknown) => {
      logger.error.andTrackError(error instanceof Error ? error : new Error(String(error)));
    });
  });
}
